import {
    useState,
    useEffect,
    useCallback,
} from 'react'

export interface ViewerFile {
    id: string
    fileName: string
    fileSize?: number
    mimeType?: string
    createdAt?: string | Date
}

type FileKind =
    | 'image'
    | 'pdf'
    | 'office'
    | 'json'
    | 'csv'
    | 'code'
    | 'text'
    | 'other'

const IMAGE_EXTS = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'svg',
    'avif',
])

const OFFICE_EXTS = new Set([
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
])

const JSON_EXTS = new Set([
    'json',
])

const CSV_EXTS = new Set([
    'csv',
])

const CODE_EXTS = new Set([
    'js',
    'jsx',
    'ts',
    'tsx',
    'py',
    'rs',
    'go',
    'java',
    'c',
    'cpp',
    'h',
    'hpp',
    'html',
    'css',
    'scss',
    'sql',
    'xml',
    'yaml',
    'yml',
    'sh',
    'bash',
])

const TEXT_EXTS = new Set([
    'txt',
    'log',
    'md',
])

function getExtension(fileName: string) {
    return fileName
        .split('.')
        .pop()
        ?.toLowerCase() ?? ''
}

function getFileKind(file: ViewerFile): FileKind {

    const ext = getExtension(file.fileName)

    if (IMAGE_EXTS.has(ext)) {
        return 'image'
    }

    if (ext === 'pdf') {
        return 'pdf'
    }

    if (OFFICE_EXTS.has(ext)) {
        return 'office'
    }

    if (JSON_EXTS.has(ext)) {
        return 'json'
    }

    if (CSV_EXTS.has(ext)) {
        return 'csv'
    }

    if (CODE_EXTS.has(ext)) {
        return 'code'
    }

    if (TEXT_EXTS.has(ext)) {
        return 'text'
    }

    return 'other'
}

function escapeHtml(str: string) {
    return str
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
}

function prettyJson(text: string) {
    try {
        return JSON.stringify(
            JSON.parse(text),
            null,
            2
        )
    } catch {
        return text
    }
}

function parseCsv(text: string): string[][] {
    return text
        .split('\n')
        .map(row =>
            row
                .split(',')
                .map(cell => cell.trim())
        )
}

export interface DocumentViewerProps {
    file: ViewerFile | null
    onClose: () => void
}

export function DocumentViewer({
    file,
    onClose,
}: DocumentViewerProps) {

    const [loading, setLoading] = useState(true)

    const [textContent, setTextContent] =
        useState<string>('')

    const [csvRows, setCsvRows] =
        useState<string[][]>([])

    useEffect(() => {
        setLoading(true)
        setTextContent('')
        setCsvRows([])
    }, [file?.id])

    useEffect(() => {

        if (!file) return

        const previous =
            document.body.style.overflow

        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previous
        }

    }, [file])

    useEffect(() => {

        if (!file) return

        const kind = getFileKind(file)

        if (
            kind !== 'json' &&
            kind !== 'csv' &&
            kind !== 'code' &&
            kind !== 'text'
        ) {
            return
        }

        const fetchContent = async () => {

            try {

                const res = await fetch(
                    `/upload/download/${file.id}`
                )

                const text = await res.text()

                if (kind === 'json') {
                    setTextContent(prettyJson(text))
                }
                else if (kind === 'csv') {
                    setCsvRows(parseCsv(text))
                }
                else {
                    setTextContent(text)
                }

            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }

        }

        fetchContent()

    }, [file])

    if (!file) {
        return null
    }

    const kind = getFileKind(file)

    const downloadUrl =
        `/upload/download/${file.id}`

    const officeUrl =
        `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
            window.location.origin + downloadUrl
        )}`

    return (
        <>
            {/* BACKDROP */}
            <div
                className="fixed inset-0 bg-black/60 z-40"
                onClick={onClose}
            />

            {/* PANEL */}
            <div
                className="
                    fixed
                    top-0
                    right-0
                    z-9999
                    bg-background
                    border-l
                    border-border
                    flex
                    flex-col
                    w-full
                    h-full
                    lg:w-[45vw]
                "
            >

                {/* HEADER */}
                <div
                    className="
                        h-14
                        shrink-0
                        border-b
                        border-border
                        px-4
                        flex
                        items-center
                        justify-between
                        gap-4
                    "
                >

                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">
                            {file.fileName}
                        </p>

                        <p className="text-xs text-muted-foreground">
                            {getExtension(file.fileName).toUpperCase()}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">

                        {/* DOWNLOAD */}
                        <a
                            href={downloadUrl}
                            download={file.fileName}
                            className="
                                h-9
                                px-3
                                border
                                border-border
                                inline-flex
                                items-center
                                text-sm
                                font-medium
                                hover:bg-muted
                            "
                        >
                            Download
                        </a>

                        {/* CLOSE */}
                        <button
                            onClick={onClose}
                            className="
                                h-9
                                w-9
                                border
                                border-border
                                inline-flex
                                items-center
                                justify-center
                                hover:bg-muted
                            "
                        >
                            ✕
                        </button>

                    </div>

                </div>

                {/* CONTENT */}
                <div className="flex-1 overflow-auto bg-muted/20 relative">

                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
                            Loading...
                        </div>
                    )}

                    {/* IMAGE */}
                    {kind === 'image' && (
                        <div className="w-full h-full flex items-center justify-center p-4">
                            <img
                                src={downloadUrl}
                                alt={file.fileName}
                                className="max-w-full max-h-full object-contain"
                                onLoad={() => setLoading(false)}
                            />
                        </div>
                    )}

                    {/* PDF */}
                    {kind === 'pdf' && (
                        <iframe
                            src={downloadUrl}
                            className="w-full h-full"
                            onLoad={() => setLoading(false)}
                        />
                    )}

                    {/* OFFICE */}
                    {kind === 'office' && (
                        <iframe
                            src={officeUrl}
                            className="w-full h-full"
                            onLoad={() => setLoading(false)}
                        />
                    )}

                    {/* JSON */}
                    {kind === 'json' && !loading && (
                        <pre
                            className="
                                p-4
                                text-xs
                                leading-6
                                overflow-auto
                                whitespace-pre
                                font-mono
                            "
                        >
                            <code>
                                {textContent}
                            </code>
                        </pre>
                    )}

                    {/* CODE */}
                    {kind === 'code' && !loading && (
                        <pre
                            className="
                                p-4
                                text-xs
                                leading-6
                                overflow-auto
                                whitespace-pre
                                font-mono
                            "
                        >
                            <code>
                                {textContent}
                            </code>
                        </pre>
                    )}

                    {/* TEXT */}
                    {kind === 'text' && !loading && (
                        <pre
                            className="
                                p-4
                                text-xs
                                leading-6
                                overflow-auto
                                whitespace-pre-wrap
                                font-mono
                            "
                        >
                            <code>
                                {textContent}
                            </code>
                        </pre>
                    )}

                    {/* CSV */}
                    {kind === 'csv' && !loading && (
                        <div className="overflow-auto">
                            <table className="min-w-full border-collapse text-sm">

                                <tbody>

                                    {csvRows.map((row, rowIndex) => (

                                        <tr
                                            key={rowIndex}
                                            className="border-b border-border"
                                        >

                                            {row.map((cell, cellIndex) => (

                                                <td
                                                    key={cellIndex}
                                                    className="
                                                        px-3
                                                        py-2
                                                        border-r
                                                        border-border
                                                        whitespace-nowrap
                                                    "
                                                >
                                                    {cell}
                                                </td>

                                            ))}

                                        </tr>

                                    ))}

                                </tbody>

                            </table>
                        </div>
                    )}

                    {/* OTHER */}
                    {kind === 'other' && !loading && (
                        <div className="h-full flex flex-col items-center justify-center gap-4">

                            <p className="text-sm">
                                Preview unavailable
                            </p>

                            <a
                                href={downloadUrl}
                                download={file.fileName}
                                className="
                                    h-10
                                    px-4
                                    border
                                    border-border
                                    inline-flex
                                    items-center
                                "
                            >
                                Download File
                            </a>

                        </div>
                    )}

                </div>

                {/* FOOTER */}
                <div
                    className="
                        h-10
                        shrink-0
                        border-t
                        border-border
                        px-4
                        flex
                        items-center
                        justify-between
                        text-xs
                        text-muted-foreground
                    "
                >

                    <span>
                        {file.fileSize
                            ? `${(file.fileSize / 1024).toFixed(1)} KB`
                            : ''}
                    </span>

                    <span>
                        {file.createdAt
                            ? new Date(file.createdAt)
                                .toLocaleDateString()
                            : ''}
                    </span>

                </div>

            </div>
        </>
    )
}

export function ViewerShift({
    isOpen,
    children,
}: {
    isOpen: boolean
    children: React.ReactNode
}) {

    return (
        <div
            className="min-w-0"
            style={{
                transition: 'padding-right 0.2s ease',
                paddingRight:
                    isOpen
                        ? '45vw'
                        : '0px',
            }}
        >
            {children}
        </div>
    )
}

export function useDocumentViewer() {

    const [viewerFile, setViewerFile] =
        useState<ViewerFile | null>(null)

    const open = useCallback(
        (file: ViewerFile) => {
            setViewerFile(file)
        },
        []
    )

    const close = useCallback(() => {
        setViewerFile(null)
    }, [])

    return {
        viewerFile,
        open,
        close,
        isOpen: viewerFile !== null,
    }
}

export function ViewButton({
    file,
    onView,
}: {
    file: ViewerFile
    onView: (file: ViewerFile) => void
}) {

    return (
        <button
            onClick={() => onView(file)}
            className="
                h-8
                px-3
                border
                border-border
                inline-flex
                items-center
                gap-2
                text-xs
                font-medium
                hover:bg-muted
            "
        >
            View
        </button>
    )
}
